/**
 * Quiet Authority loading skeletons.
 *
 * Hairline-framed shimmer blocks in parchment tones, shaped to roughly match
 * the layout that's loading rather than a generic spinner or a bare line of
 * muted text. The shimmer honours `prefers-reduced-motion` via the
 * `.qa-shimmer` rule in index.css (the sweep is dropped, a static muted block
 * stays). Replaces the ad-hoc "Loading …" strings across the app.
 */

function Block({ className = '' }: { className?: string }) {
  return <div className={`qa-shimmer ${className}`} />;
}

function Masthead() {
  return (
    <div className="mb-8">
      <Block className="h-2.5 w-32 mb-3" />
      <Block className="h-9 w-2/5 mb-3" />
      <Block className="h-3 w-1/2" />
    </div>
  );
}

function StatStrip() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-6 border-t border-b border-hairline mb-8">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="qa-stat">
          <Block className="h-2.5 w-20" />
          <Block className="h-7 w-24 mt-2" />
        </div>
      ))}
    </div>
  );
}

function TableBlock({ rows }: { rows: number }) {
  return (
    <div className="border border-hairline">
      <div className="bg-paper border-b border-hairline px-3 py-3 flex gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Block key={i} className="h-2.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="px-3 py-3 flex gap-4 border-b border-hairline last:border-b-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <Block key={i} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

function CardsBlock({ rows }: { rows: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="qa-card">
          <div className="px-4 py-2 border-b border-hairline-paper flex justify-between">
            <Block className="h-2.5 w-44" />
            <Block className="h-2.5 w-24" />
          </div>
          <div className="p-4 space-y-3">
            <Block className="h-5 w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <Block className="h-3 w-full" />
              <Block className="h-3 w-full" />
              <Block className="h-3 w-2/3" />
              <Block className="h-3 w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function QASkeleton({
  variant = 'cards',
  rows,
}: {
  variant?: 'dashboard' | 'detail' | 'table' | 'cards';
  rows?: number;
}) {
  if (variant === 'dashboard') {
    return (
      <div aria-busy="true" aria-live="polite">
        <Masthead />
        <StatStrip />
        <TableBlock rows={rows ?? 10} />
      </div>
    );
  }
  if (variant === 'detail') {
    return (
      <div aria-busy="true" aria-live="polite">
        <Masthead />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="qa-card p-6 space-y-3">
              <Block className="h-2.5 w-24 mb-2" />
              {Array.from({ length: 5 }).map((_, j) => (
                <Block key={j} className="h-3 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (variant === 'table') {
    return (
      <div aria-busy="true" aria-live="polite">
        <TableBlock rows={rows ?? 8} />
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-live="polite">
      <CardsBlock rows={rows ?? 3} />
    </div>
  );
}
